'use client';

import { useFormatter, useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import {
  NOTIFICATIONS_PAGE_SIZE,
  type NotificationItem
} from '@/api/backend/contract/Notification';
import {
  getNotifications,
  markNotificationAsRead
} from '@/api/backend/service/notifications';
import { UiNotificationTile } from '@/ui/notification-tile';
import { UiKitButton } from '@/ui-kit/button';
import { UiKitLoader } from '@/ui-kit/loader';

import { styles } from './FeatureAccountNotifications.styles';
import { MOCK_NOTIFICATIONS } from './mockNotifications';
import { groupNotificationsByDate } from './utils/groupNotificationsByDate';

const IS_DEV = process.env.NODE_ENV === 'development';

export interface AccountNotificationsProps {
  initialList: NotificationItem[];
  initialTotal: number;
  variant?: 'page' | 'drawer';
}

export function AccountNotifications({
  initialList,
  initialTotal,
  variant = 'page'
}: AccountNotificationsProps) {
  const t = useTranslations('accountNotifications');
  const format = useFormatter();
  const slots = styles({ variant });
  const [useMock, setUseMock] = useState(false);
  const [apiNotifications, setApiNotifications] = useState(initialList);
  const [readIds, setReadIds] = useState<Set<string>>(() => new Set());
  const [total, setTotal] = useState(initialTotal);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const apiNotificationsRef = useRef(apiNotifications);
  const useMockRef = useRef(useMock);
  const loadMoreRef = useRef<() => Promise<void>>(async () => undefined);

  const notifications = (useMock ? MOCK_NOTIFICATIONS : apiNotifications).map(
    (item) => (readIds.has(item.id) ? { ...item, isRead: true } : item)
  );
  const groups = groupNotificationsByDate(notifications);
  const hasMore = !useMock && apiNotifications.length < total;

  useEffect(() => {
    hasMoreRef.current = hasMore;
    apiNotificationsRef.current = apiNotifications;
    useMockRef.current = useMock;
    isLoadingMoreRef.current = isLoadingMore;
  }, [hasMore, apiNotifications, useMock, isLoadingMore]);

  useEffect(() => {
    loadMoreRef.current = async () => {
      if (useMockRef.current || isLoadingMoreRef.current || !hasMoreRef.current) return;

      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);

      const offset = apiNotificationsRef.current.length;
      const response = await getNotifications({
        limit: NOTIFICATIONS_PAGE_SIZE,
        offset
      });

      if (!response.isError && response.data) {
        const nextItems = response.data.items;
        setApiNotifications((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          return [...prev, ...nextItems.filter((item) => !existingIds.has(item.id))];
        });

        if (typeof response.data.total === 'number') {
          setTotal(response.data.total);
        } else if (nextItems.length < NOTIFICATIONS_PAGE_SIZE) {
          setTotal(offset + nextItems.length);
        }
      } else {
        setTotal(apiNotificationsRef.current.length);
      }

      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    };
  }, []);

  useEffect(() => {
    if (!hasMore) return;

    const element = loadMoreTriggerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        if (isLoadingMoreRef.current) return;
        if (!hasMoreRef.current) return;

        void loadMoreRef.current();
      },
      {
        rootMargin: '200px 0px',
        threshold: 0
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [hasMore]);

  useEffect(() => {
    if (!hasMore || isLoadingMore) return;

    const element = loadMoreTriggerRef.current;
    if (!element) return;

    const frameId = requestAnimationFrame(() => {
      const { top, bottom } = element.getBoundingClientRect();
      const viewportBottom = window.innerHeight + 200;

      if (top < viewportBottom && bottom > 0) {
        void loadMoreRef.current();
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [hasMore, isLoadingMore, apiNotifications.length]);

  useEffect(() => {
    setApiNotifications(initialList);
    setTotal(initialTotal);
  }, [initialList, initialTotal]);

  const handleNotificationClick = (notification: NotificationItem) => {
    if (notification.isRead || readIds.has(notification.id)) {
      return;
    }

    setReadIds((prev) => new Set(prev).add(notification.id));

    if (!useMock) {
      void markNotificationAsRead(notification.id).then((result) => {
        if (result.isError) return;

        window.dispatchEvent(new CustomEvent('notifications:messages-read'));
      });
    }
  };

  const header = (
    <div className={slots.header()}>
      <h1 className={slots.title()}>{t('title')}</h1>
      {IS_DEV && variant === 'page' && (
        <UiKitButton
          color="ghost"
          onClick={() => setUseMock((prev) => !prev)}
        >
          {useMock ? t('devHideMock') : t('devShowMock')}
        </UiKitButton>
      )}
    </div>
  );

  if (notifications.length === 0) {
    return (
      <div className={slots.base()}>
        {header}
        <p className={slots.empty()}>{t('emptyDescription')}</p>
      </div>
    );
  }

  return (
    <div className={slots.base()}>
      {header}
      <div className={slots.list()}>
        {groups.map((group) => (
          <section
            key={group.dateKey}
            className={slots.group()}
          >
            <h2 className={slots.date()}>
              {format.dateTime(group.date, {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </h2>
            <div className={slots.cards()}>
              {group.items.map((notification) => (
                <UiNotificationTile
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
      {hasMore && (
        <div className={slots.loadMore()}>
          <div
            ref={loadMoreTriggerRef}
            className={slots.loadMoreTrigger()}
          />
          <UiKitLoader show={isLoadingMore} />
        </div>
      )}
    </div>
  );
}
